import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Modal, Tooltip, Typography } from 'antd';
import { mdiClose } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { UI54Table } from '@agpl/ui54kit';

import { ApiContext } from '../../api/ApiContextProvider';
import { antNotification, capitalize } from '../../mainUtils';
import { dateFormat54origins } from '../../54origins/utils54origins';
import AntButtonWithMDI from '../../components/AntButtonWithMDI';

const ACTION_CLASS_LABELS = {
  ServiceCreateActor: 'Create Actor',
  ServiceUpdateActor: 'Update Actor',
  ServiceDeleteActor: 'Delete Actor',
  ServiceCreatePermAction: 'Create Permission',
  ServiceUpdatePermAction: 'Update Permission',
  ServiceDeletePermAction: 'Delete Permission',
  ServiceResetSessions: 'Reset Sessions',
};

function SyncPackagesModal({ serviceUUID, serviceName, onClose }) {
  const { t } = useTranslation();
  const { requestGetSyncPackagesForService, requestGetSyncHistoryForService } = useContext(ApiContext);

  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchPackages = useCallback(() => {
    if (!serviceUUID) return;
    setLoading(true);
    requestGetSyncPackagesForService(serviceUUID)
      .then((data) => {
        setPackages(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        antNotification.error(capitalize(t('auth.notifications.response_error', 'data retrieval error')));
      })
      .finally(() => setLoading(false));
  }, [serviceUUID, requestGetSyncPackagesForService, t]);

  const fetchHistory = useCallback(() => {
    if (!serviceUUID) return;
    setHistoryLoading(true);
    requestGetSyncHistoryForService(serviceUUID)
      .then((data) => {
        setHistory(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        antNotification.error(capitalize(t('auth.notifications.response_error', 'data retrieval error')));
      })
      .finally(() => setHistoryLoading(false));
  }, [serviceUUID, requestGetSyncHistoryForService, t]);

  useEffect(() => {
    if (!serviceUUID) return;
    fetchPackages();
    fetchHistory();
    const interval = setInterval(() => {
      fetchPackages();
      fetchHistory();
    }, 5000);
    return () => clearInterval(interval);
  }, [serviceUUID, fetchPackages, fetchHistory]);

  const columns = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'ID',
      width: 70,
      className: 'p-2',
    },
    {
      dataIndex: 'action_class',
      key: 'action_class',
      title: capitalize(t('auth.headers.action', 'action')),
      className: 'p-2',
      render: (cell) => ACTION_CLASS_LABELS[cell] || cell,
    },
    {
      dataIndex: 'object_table',
      key: 'object_table',
      title: capitalize(t('auth.headers.object_table', 'object table')),
      className: 'p-2',
    },
    {
      dataIndex: 'actor_type',
      key: 'actor_type',
      title: capitalize(t('auth.headers.actor_type', 'actor type')),
      className: 'p-2',
      render: (cell) => cell || '—',
    },
    {
      dataIndex: 'object_uuid',
      key: 'object_uuid',
      title: capitalize(t('auth.headers.object_uuid', 'object UUID')),
      className: 'p-2',
      render: (cell) => (
        <Tooltip title={cell}>
          <span className="font-mono text-xs">{cell ? `${cell.slice(0, 8)}…` : '—'}</span>
        </Tooltip>
      ),
    },
    {
      dataIndex: 'created',
      key: 'created',
      title: capitalize(t('auth.headers.created', 'created')),
      className: 'p-2',
      width: 140,
      render: (cell) => cell && dateFormat54origins(cell).dateWithTime,
    },
    {
      dataIndex: 'request_count',
      key: 'request_count',
      title: capitalize(t('auth.headers.attempts', 'attempts')),
      className: 'p-2',
      width: 90,
      render: (cell) => <span className={cell > 1 ? 'text-red' : ''}>{cell}</span>,
    },
    {
      dataIndex: 'stop_updating',
      key: 'stop_updating',
      title: capitalize(t('auth.headers.stopped', 'stopped')),
      className: 'p-2',
      width: 90,
      render: (cell) => (
        <span className={cell ? 'text-red' : 'text-green'}>
          {cell ? capitalize(t('auth.labels.yes', 'yes')) : capitalize(t('auth.labels.no', 'no'))}
        </span>
      ),
    },
  ];

  const historyColumns = [
    {
      dataIndex: 'object_table',
      key: 'object_table',
      title: capitalize(t('auth.headers.object_table', 'object table')),
      className: 'p-2',
    },
    {
      dataIndex: 'action_class',
      key: 'action_class',
      title: capitalize(t('auth.headers.action', 'action')),
      className: 'p-2',
      render: (cell) => ACTION_CLASS_LABELS[cell] || cell,
    },
    {
      dataIndex: 'object_uuid',
      key: 'object_uuid',
      title: capitalize(t('auth.headers.object_uuid', 'object UUID')),
      className: 'p-2',
      render: (cell) => (
        <Tooltip title={cell}>
          <span className="font-mono text-xs">{cell ? `${cell.slice(0, 8)}…` : '—'}</span>
        </Tooltip>
      ),
    },
    {
      dataIndex: 'created',
      key: 'created',
      title: capitalize(t('auth.headers.created', 'created')),
      className: 'p-2',
      width: 140,
      render: (cell) => cell && dateFormat54origins(cell).dateWithTime,
    },
  ];

  return (
    <Modal
      title={`${capitalize(t('auth.headers.unsynced_packages', 'unsynchronized packages'))} — ${serviceName || serviceUUID}`}
      open={!!serviceUUID}
      onCancel={onClose}
      width="80%"
      destroyOnClose
      footer={[
        <AntButtonWithMDI
          key="close"
          className="button-secondary-outlined"
          onClick={onClose}
          pathForMdi={mdiClose}
          label={capitalize(t('auth.buttons.close', 'close'))}
        />,
      ]}
    >
      <UI54Table
        dataSource={packages}
        columns={columns}
        rowKey="id"
        loading={loading}
        bordered
        useDefaultPagination
        pagination={{
          pageSizeOptions: [25, 50],
          defaultPageSize: 25,
        }}
      />
      <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
        {capitalize(t('auth.headers.sync_history', 'sync history'))}
      </Typography.Title>
      <UI54Table
        dataSource={history}
        columns={historyColumns}
        rowKey={(row, idx) => `${row.object_uuid}-${row.created}-${idx}`}
        loading={historyLoading}
        bordered
        useDefaultPagination
        pagination={{
          pageSizeOptions: [25, 50],
          defaultPageSize: 25,
        }}
      />
    </Modal>
  );
}

SyncPackagesModal.propTypes = {
  serviceUUID: PropTypes.string,
  serviceName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default SyncPackagesModal;
